import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Users, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-states';
import { useDepartments } from '@/hooks/useDepartments';
import { useDepartmentMonthSchedule } from '@/hooks/schedules/useDepartmentSchedule';
import { ScheduleGrid } from './ScheduleGrid';
import type { ScheduleDay, EmployeeSchedule } from '@/types/schedule.types';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

// Data passed to CreateExceptionDialog for replacement filtering
export interface CreateExceptionData {
  employeeId: string;
  date: string;
  employeeName: string;
  // All employees with their schedules for the selected date
  employeesWithSchedule: Array<{
    id: string;
    name: string;
    position?: string | null;
    isWorkingDay: boolean;
    shiftStart?: string | null;
    shiftEnd?: string | null;
  }>;
}

interface DepartmentScheduleViewProps {
  onCreateException?: (data: CreateExceptionData) => void;
  onViewException?: (exceptionId: string) => void;
}

export const DepartmentScheduleView: React.FC<DepartmentScheduleViewProps> = ({
  onCreateException,
  onViewException,
}) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);

  // Fetch departments
  const { data: departmentsData, isLoading: isLoadingDepartments } = useDepartments({
    page: 1,
    limit: 100,
  });

  // Fetch department schedule
  const { data: scheduleData, isLoading: isLoadingSchedule } = useDepartmentMonthSchedule(
    selectedDepartmentId,
    year,
    month,
  );

  // Generate dates for the month
  const dates = useMemo(() => {
    const result: Date[] = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      result.push(new Date(d));
    }

    return result;
  }, [year, month]);

  // Navigate months
  const goToPrevMonth = () => {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else {
      setMonth(month + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  // Handle cell click
  const handleCellClick = (employee: EmployeeSchedule, day: ScheduleDay) => {
    if (day.exception) {
      // View existing exception
      onViewException?.(day.exception.id);
    } else {
      // Create new exception - pass all employees with their schedules for this date
      const employeesWithSchedule = scheduleData?.employees.map((emp) => {
        const empDaySchedule = emp.schedule.find((s) => s.date === day.date);
        return {
          id: emp.employee_id,
          name: `${emp.last_name} ${emp.first_name}`,
          position: emp.position,
          isWorkingDay: empDaySchedule?.is_working_day ?? false,
          shiftStart: empDaySchedule?.shift_start,
          shiftEnd: empDaySchedule?.shift_end,
        };
      }) ?? [];

      onCreateException?.({
        employeeId: employee.employee_id,
        date: day.date,
        employeeName: `${employee.last_name} ${employee.first_name}`,
        employeesWithSchedule,
      });
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!scheduleData) return { totalEmployees: 0, workingToday: 0, exceptionsToday: 0 };

    const today = new Date().toISOString().split('T')[0];
    let workingToday = 0;
    let exceptionsToday = 0;

    for (const emp of scheduleData.employees) {
      const todaySchedule = emp.schedule.find((s) => s.date === today);
      if (todaySchedule?.is_working_day) workingToday++;
      if (todaySchedule?.source === 'schedule_exception') exceptionsToday++;
    }

    return {
      totalEmployees: scheduleData.total_employees,
      workingToday,
      exceptionsToday,
    };
  }, [scheduleData]);

  return (
    <div className="space-y-4">
      {/* Header with department selector and navigation */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {/* Department selector */}
          <Select
            value={selectedDepartmentId || ''}
            onValueChange={(value) => setSelectedDepartmentId(value || null)}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Выберите отдел" />
            </SelectTrigger>
            <SelectContent>
              {isLoadingDepartments ? (
                <div className="p-2 flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                departmentsData?.data?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-[180px] justify-center">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              {MONTH_NAMES[month]} {year}
            </span>
          </div>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Сегодня
          </Button>
        </div>
      </div>

      {/* Statistics cards */}
      {selectedDepartmentId && scheduleData && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Сотрудников</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEmployees}</div>
              <p className="text-xs text-muted-foreground">
                в отделе {scheduleData.department_name}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Работают сегодня</CardTitle>
              <Calendar className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.workingToday}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalEmployees - stats.workingToday} выходных
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Исключений сегодня</CardTitle>
              <Plus className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{stats.exceptionsToday}</div>
              <p className="text-xs text-muted-foreground">изменения в графике</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule grid */}
      {!selectedDepartmentId ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Выберите отдел для просмотра графика</p>
            </div>
          </CardContent>
        </Card>
      ) : isLoadingSchedule ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </CardContent>
        </Card>
      ) : scheduleData ? (
        <ScheduleGrid
          employees={scheduleData.employees}
          dates={dates}
          onCellClick={handleCellClick}
        />
      ) : null}
    </div>
  );
};

export default DepartmentScheduleView;
